import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuthStore } from '../contexts/authStore';
import * as api from '../services/api';
import { initSocket, getSocket } from '../services/socket';
import ScheduleMeetingDialog from '../components/ScheduleMeetingDialog';
import MeetingDetailsDialog from '../components/MeetingDetailsDialog';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    loadMeetings();
  }, []);

  // Listen for real-time RSVP updates
  useEffect(() => {
    // Initialize socket connection for dashboard updates
    const socket = initSocket(token);

    const handleRsvpUpdate = (data) => {
      console.log('[Dashboard] RSVP update received:', data);

      // Update the meeting's invite status in local state
      setMeetings(prevMeetings =>
        prevMeetings.map(meeting => {
          if (meeting.id === data.meetingId || meeting.code === data.meetingCode) {
            let newAcceptedCount = meeting.acceptedCount || 0;

            // If we have the invites array (from create or details), update it
            if (meeting.invites) {
              const updatedInvites = meeting.invites.map(invite =>
                invite.id === data.inviteId || invite.email === data.email
                  ? { ...invite, status: data.status }
                  : invite
              );
              newAcceptedCount = updatedInvites.filter(i => i.status === 'accepted').length;
              return { ...meeting, invites: updatedInvites, acceptedCount: newAcceptedCount };
            }

            // No invites array (loaded from my-meetings API) - adjust count based on status change
            // We track by email to avoid double counting
            const prevStatus = meeting._rsvpStatuses?.[data.email];
            const wasAccepted = prevStatus === 'accepted';
            const isNowAccepted = data.status === 'accepted';

            if (wasAccepted && !isNowAccepted) {
              newAcceptedCount = Math.max(0, newAcceptedCount - 1);
            } else if (!wasAccepted && isNowAccepted) {
              newAcceptedCount = newAcceptedCount + 1;
            }

            // Track status for future updates
            const _rsvpStatuses = { ...(meeting._rsvpStatuses || {}), [data.email]: data.status };

            return { ...meeting, acceptedCount: newAcceptedCount, _rsvpStatuses };
          }
          return meeting;
        })
      );

      // Also update selectedMeeting if it's the one being viewed
      setSelectedMeeting(prev => {
        if (prev && (prev.id === data.meetingId || prev.code === data.meetingCode)) {
          const updatedInvites = prev.invites?.map(invite =>
            invite.id === data.inviteId || invite.email === data.email
              ? { ...invite, status: data.status }
              : invite
          );
          return { ...prev, invites: updatedInvites };
        }
        return prev;
      });
    };

    socket.on('rsvp:updated', handleRsvpUpdate);

    return () => {
      socket.off('rsvp:updated', handleRsvpUpdate);
    };
  }, [token]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      const response = await api.getUserMeetings(token);
      setMeetings(response.meetings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeeting = async (data) => {
    const response = await api.createMeeting(data, token);

    // Add inviteCount and acceptedCount for immediate display
    const meetingWithCounts = {
      ...response.meeting,
      inviteCount: response.meeting.invites?.length || 0,
      acceptedCount: response.meeting.invites?.filter(i => i.status === 'accepted').length || 0,
    };
    setMeetings([meetingWithCounts, ...meetings]);

    // If scheduled meeting with invites sent, show success message
    if (data.scheduledAt && data.sendInvites && response.meeting.invites?.length > 0) {
      setSuccessMessage(`Meeting created! ${response.meeting.invites.length} invite(s) sent.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } else if (data.scheduledAt) {
      // Scheduled but no invites - just show the link
      setSuccessMessage('Meeting scheduled! Copy the link to share with participants.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } else {
      // Instant meeting - navigate directly
      navigate(`/meeting/${response.meeting.code}`);
    }
  };

  const handleStartInstantMeeting = async () => {
    try {
      setCreating(true);
      const response = await api.createMeeting('Instant Meeting', token);
      navigate(`/meeting/${response.meeting.code}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`https://videomeet.bitscale.co.uk/${code}`);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'waiting':
        return 'warning';
      case 'ended':
        return 'default';
      default:
        return 'default';
    }
  };

  const openMeetingDetails = (meeting) => {
    setSelectedMeeting(meeting);
    setDetailsDialogOpen(true);
  };

  const handleMeetingUpdate = (updatedMeeting) => {
    setMeetings(prevMeetings => prevMeetings.map((m) =>
      m.code === updatedMeeting.code
        ? {
            ...m,
            title: updatedMeeting.title,
            scheduledAt: updatedMeeting.scheduledAt,
            customMessage: updatedMeeting.customMessage,
            inviteCount: updatedMeeting.invites?.length || m.inviteCount,
            acceptedCount: updatedMeeting.invites?.filter((i) => i.status === 'accepted').length || 0,
          }
        : m
    ));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Welcome, {user?.displayName}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Start or schedule your video meetings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Quick actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(0,115,168,0.2)',
              },
            }}
            onClick={handleStartInstantMeeting}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0693e3 0%, #9b51e0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <VideoCallIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Start Instant Meeting
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Begin a meeting right now
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
              },
            }}
            onClick={() => setScheduleDialogOpen(true)}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'surface.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <AddIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Create Meeting
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Schedule and invite participants
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Meetings list */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Your Meetings
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : meetings.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <ScheduleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No meetings yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
            Create your first meeting to get started
          </Typography>
          <Button variant="contained" onClick={() => setScheduleDialogOpen(true)}>
            Create Meeting
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {meetings.map((meeting) => (
            <Grid item xs={12} key={meeting.id}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {meeting.title}
                      </Typography>
                      <Chip
                        label={meeting.status}
                        color={getStatusColor(meeting.status)}
                        size="small"
                      />
                      {meeting.scheduledAt && (
                        <Chip
                          icon={<EventIcon />}
                          label={new Date(meeting.scheduledAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {meeting.inviteCount > 0 && (
                        <Chip
                          icon={<PeopleIcon />}
                          label={`${meeting.acceptedCount}/${meeting.inviteCount}`}
                          size="small"
                          variant="outlined"
                          color={meeting.acceptedCount > 0 ? 'success' : 'default'}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Code: {meeting.code} | Created: {new Date(meeting.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  <Tooltip title="Gestisci">
                    <IconButton onClick={() => openMeetingDetails(meeting)}>
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={copied === meeting.code ? 'Copied!' : 'Copy link'}>
                    <IconButton onClick={() => copyLink(meeting.code)}>
                      <ContentCopyIcon color={copied === meeting.code ? 'success' : 'inherit'} />
                    </IconButton>
                  </Tooltip>

                  {meeting.status !== 'ended' && (
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/meeting/${meeting.code}`)}
                    >
                      {meeting.status === 'active' ? 'Rejoin' : 'Start'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Schedule meeting dialog */}
      <ScheduleMeetingDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onSubmit={handleCreateMeeting}
      />

      {/* Meeting details dialog */}
      <MeetingDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        meeting={selectedMeeting}
        token={token}
        onUpdate={handleMeetingUpdate}
      />
    </Container>
  );
}
